import {
  REVIEWABLE_DOCUMENT_CATEGORIES,
  REVIEWABLE_DOCUMENT_TYPES,
} from '@bordrio/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { differenceInDays, isBefore, isValid, parse } from 'date-fns';
import _ from 'lodash';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { Alert, DateInput, SelectInput, TextInput } from '@src/components';
import InfoTooltip from '@src/components/InfoTooltip';
import ReactSelectInput from '@src/components/ReactSelectInput';
import { COUNTRIES, getCountriesTranslated } from '@src/constants/countries';
import {
  GetOrdersDocument,
  NifOrderFragment,
  useCreateNifOrderMutation,
  useUpdateNifOrderMutation,
} from '@src/generated/graphql';
import { useRequiredUserId } from '@src/hooks/useRequiredUserId';
import { useStatus } from '@src/hooks/useStatus';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import { getApolloErrorCode } from '@src/utils/apollo-error';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import {
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
} from '../../constants/options';
import { VALIDATIONS } from '../../utils/validation';
import { NifOrderStepPageWithOptionalOrderProps } from '../types/stepProps';

const NIF_FORM_PROPS: readonly (keyof NifOrderFragment)[] = [
  'givenNames',
  'surname',
  'birthDate',
  'birthCountry',
  'documentType',
  'documentNumber',
  'documentIssuingCountry',
  'documentExpirationDate',
  'gender',
  'maritalStatus',
] as const;

const validateDateTest = (dateStr?: string): boolean => {
  try {
    if (!dateStr) {
      return true;
    }
    const result = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isValid(result);
  } catch {
    return false;
  }
};

const dateIsAtLeast1WeekFromNow = (dateStr: string): boolean => {
  try {
    const testedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
    return differenceInDays(testedDate, new Date()) > 7;
  } catch {
    return false;
  }
};

const dateIsBeforeToday = (dateStr: string): boolean => {
  try {
    const testedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isBefore(testedDate, new Date().setHours(0, 0, 0, 0));
  } catch {
    return false;
  }
};

// t('nif:validation.date', 'Enter a valid date')
// t('nif:validation.birth_date', 'Invalid birth date')
// t('nif:validation.birth_country', 'Enter your country of birth')
// t('nif:validation.document_number', 'Enter the document number')
// t('nif:validation.document_issuing_country', 'Enter the document issuing country')
// t('nif:validation.document_expiration_date', 'Enter the document expiration date')
// t('nif:validation.document_about_to_expire', 'The document has expired or is about to expire')

const formSchema = z.object({
  givenNames: VALIDATIONS.requiredStringWithoutSpecialChars,
  surname: VALIDATIONS.requiredStringWithoutSpecialChars,
  birthDate: z
    .string({ required_error: 'nif:validation.date' })
    .refine(validateDateTest, 'nif:validation.date')
    .refine(dateIsBeforeToday, 'nif:validation.birth_date'),
  birthCountry: z.enum(COUNTRIES.map((c) => c.code) as [string, ...string[]], {
    required_error: 'nif:validation.birth_country',
  }),
  documentType: z.enum(['PASSPORT', 'EU_ID']).optional(),
  documentNumber: z.string().min(1, 'nif:validation.document_number'),
  documentIssuingCountry: z.enum(
    COUNTRIES.map((c) => c.code) as [string, ...string[]],
    {
      required_error: 'nif:validation.document_issuing_country',
    },
  ),
  documentExpirationDate: z
    .string({
      required_error: 'nif:validation.document_expiration_date',
    })
    .refine(validateDateTest, 'nif:validation.date')
    .refine(
      dateIsAtLeast1WeekFromNow,
      'nif:validation.document_about_to_expire',
    ),
  gender: VALIDATIONS.gender,
  maritalStatus: VALIDATIONS.maritalStatus,
  proofOfIdentityDocumentTypeId: z.string({
    required_error: 'validation.required',
  }),
});

type FormData = z.infer<typeof formSchema>;

// TODO: Clear out passport/ID if document type changes

function NifPersonalDetailsPage({
  nifOrder,
  previousUrl,
  getNextUrl,
}: NifOrderStepPageWithOptionalOrderProps): JSX.Element {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      ..._.pick(nifOrder, NIF_FORM_PROPS),
      documentType: nifOrder?.documentType as 'PASSPORT' | 'EU_ID' | undefined,
      gender: nifOrder?.gender || undefined,
      maritalStatus: nifOrder?.maritalStatus || undefined,
      proofOfIdentityDocumentTypeId:
        nifOrder?.proofOfIdentityDocumentType?.id ?? undefined,
    },
    resolver: zodResolver(formSchema),
  });
  const userId = useRequiredUserId();
  const [createNifOrder] = useCreateNifOrderMutation({
    refetchQueries: [{ query: GetOrdersDocument, variables: { userId } }],
  });
  const [updateNifOrder] = useUpdateNifOrderMutation();
  const { status, setError } = useStatus();
  const navigate = useNavigate();
  const { t, i18n: localI18n } = useTranslation(['nif']);
  const toast = useToast();

  async function onSubmit(formData: FormData): Promise<void> {
    try {
      if (!nifOrder) {
        const result = await createNifOrder({
          variables: {
            input: {
              data: {
                ...formData,
                documentType:
                  (formData.documentType ??
                  formData.proofOfIdentityDocumentTypeId ===
                    REVIEWABLE_DOCUMENT_TYPES.passport.id)
                    ? 'PASSPORT'
                    : 'EU_ID',
                userId,
              },
              withVoucher: true,
            },
          },
        });
        const newNifOrder = result?.data?.createNifOrder?.nifOrder;
        if (!newNifOrder) {
          throw new Error('createNifOrder returned null');
        }

        void navigate(getNextUrl(newNifOrder));
      } else {
        const docTypeChanged =
          nifOrder.documentType !== formData.documentType ||
          nifOrder.proofOfIdentityDocumentTypeId !==
            formData.proofOfIdentityDocumentTypeId;
        const clearProofOfIdentityPayload = !docTypeChanged
          ? undefined
          : {
              intakeDocuments: [
                {
                  documentCategoryId:
                    REVIEWABLE_DOCUMENT_CATEGORIES.nif_proof_of_identity.id,
                  reviewableDocument: null,
                },
              ],
            };
        const result = await updateNifOrder({
          variables: {
            input: {
              id: nifOrder.id,
              // we want to clear out proof of identity if the document type changes
              data: {
                ...formData,
                ...clearProofOfIdentityPayload,
              },
            },
          },
        });
        const updatedNifOrder = result?.data?.updateNifOrder?.nifOrder;
        if (!updatedNifOrder) {
          throw new Error('updateNifOrder returned null');
        }

        void navigate(getNextUrl(updatedNifOrder));
      }
    } catch (err) {
      const errorCode = getApolloErrorCode(err, ['no-voucher'] as const);
      if (errorCode === 'no-voucher') {
        toast.error(
          t(
            'errors.no_voucher',
            'You need to purchase a NIF order voucher before you can create a NIF order',
          ),
        );
        void navigate('/orders/nif/place-order');
      } else {
        setError(logAndFormatError(err));
      }
    }
  }

  const proofOfIdentityDocumentTypeOptions: {
    label: string;
    value: string;
  }[] = [
    {
      label: t('document_types.passport', 'Passport', { ns: 'translation' }),
      value: REVIEWABLE_DOCUMENT_TYPES.passport.id,
    },
    {
      label: t('document_types.eu_id', 'EU National ID card', {
        ns: 'translation',
      }),
      value: REVIEWABLE_DOCUMENT_TYPES.eu_id_card.id,
    },
  ];

  const countryOptions = useMemo(
    () =>
      getCountriesTranslated(localI18n.language).map((c) => ({
        label: c.name,
        value: c.code,
      })),
    [localI18n.language],
  );

  const manualT = t;
  // only allow male and female
  const genderOptions = Object.entries(GENDER_OPTIONS)
    .filter(([value]) => value === 'MALE' || value === 'FEMALE')
    .map(([value, label]) => ({
      value,
      label: manualT(label),
    }));

  const maritalStatusOptions = Object.entries(MARITAL_STATUS_OPTIONS).map(
    ([value, label]) => ({
      value,
      label: manualT(label),
    }),
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-2">
        <h2>{t('personal_details.title', 'Personal details')}</h2>
        <p>
          {t(
            'personal_details.instructions',
            "Input the information exactly as it is listed on the applicant's proof of identity.",
          )}
        </p>
      </div>
      <Alert.WithStatus status={status} />
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        <TextInput.LabelledController
          label={t('personal_details.given_names_label', 'Given name(s)')}
          control={control}
          name="givenNames"
        />
        <TextInput.LabelledController
          label={t('personal_details.surname_label', 'Surname')}
          control={control}
          name="surname"
        />
        <DateInput.LabelledController
          label={t(
            'personal_details.birth_date_label',
            'Date of birth (Year - Month - Day)',
          )}
          control={control}
          name="birthDate"
          autoComplete="bday"
        />
        <ReactSelectInput.LabelledController
          label={t('personal_details.birth_country_label', 'Country of birth')}
          options={countryOptions}
          control={control}
          name="birthCountry"
        />
        <SelectInput.LabelledController
          label={t('personal_details.document_type_label', 'Document type')}
          options={proofOfIdentityDocumentTypeOptions}
          control={control}
          placeholder={t(
            'personal_details.placeholder_select_document',
            'Select Document',
          )}
          name="proofOfIdentityDocumentTypeId"
        />
        <TextInput.LabelledController
          label={t('personal_details.document_number_label', 'Document number')}
          control={control}
          name="documentNumber"
        />
        <ReactSelectInput.LabelledController
          label={
            <div className="flex space-x-2">
              <span>
                {t('personal_details.issuing_country_label', 'Issuing Country')}
              </span>
              <InfoTooltip>
                {t(
                  'personal_details.tooltip_issuing_country',
                  'This is the country that issued the proof of identification document (e.g. if using a US passport, please select United States of America).',
                )}
              </InfoTooltip>
            </div>
          }
          options={countryOptions}
          control={control}
          name="documentIssuingCountry"
        />
        <DateInput.LabelledController
          label={t(
            'personal_details.expiry_date_label',
            'Date of expiration (Year - Month - Day)',
          )}
          control={control}
          name="documentExpirationDate"
        />
        <SelectInput.LabelledController
          control={control}
          options={genderOptions}
          name="gender"
          label={t('personal_details.sex', 'Sex')}
          placeholder={t('translation:placeholders.select', 'Select')}
        />
        <SelectInput.LabelledController
          control={control}
          options={maritalStatusOptions}
          name="maritalStatus"
          label={t('personal_details.marital_status', 'Marital Status')}
          placeholder={t('translation:placeholders.select', 'Select')}
        />
      </div>
      <OrderNavigationButtons
        disabled={isSubmitting}
        previousUrl={previousUrl}
        buttonType="submit"
      />
    </form>
  );
}

export default NifPersonalDetailsPage;
